import React, { useState, useCallback, useRef, useEffect, ChangeEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../../context/alert';
import { useTranslations } from '../../../hooks/useTranslations';
import { CategoryResponse } from '../../../types/api/categories';
import { SubcategoryResponse, CreateSubcategoryRequest } from '../../../types/api/catalogs';
import { createSubcategory, editSubcategory } from '../../../services/catalogs';
import { fetchLanguages } from "../../../services/lang";
import ModalSheet from '../../basic/ModalSheet';
import LangModal from './LangModal';
import { LanguageResponse } from '../../../types/api/lang';
import { Stack, Button, TextInput, Text } from '@primer/react'

interface SubcategoryModalProps {
    parentCategory: CategoryResponse
    subcategory: SubcategoryResponse | undefined
    isOpen: boolean;
    onDidSaved: () => void;
    onClose: () => void;
}

export const SubcategoryModal: React.FC<SubcategoryModalProps> = ({ parentCategory, subcategory, isOpen, onDidSaved, onClose }) => {

    const [allLangs, setAllLangs] = useState<LanguageResponse[]>([]);
    const [isPending, setIsPending] = useState<boolean>(false)
    const [openLangModal, setLangModal] = React.useState(false)

    const {
        translations,
        setTranslations,
        langs,
        setLangs,
        currentLangIndex,
        setCurrentLangIndex,
        getCurrentFieldValue,
        updateFieldValue,
        isAllLanguagesFilled,
        getRussianOrFirstFieldValue,
        langsDidUpdated
    } = useTranslations()

    const { setSuccess, setError, setInfo } = useAlert();

    const { mutate: fetchLanguagesRequest, isPending: isLoadingLang } = useMutation({
        mutationFn: fetchLanguages,
        onSuccess: (data) => {
            setAllLangs(data.list);
        },
        onError: (error: any) => {
            setAllLangs([]);
        }
    });

    const { mutate: createSubcategoryRequest } = useMutation({
        mutationFn: (data: CreateSubcategoryRequest) => createSubcategory(data),
        onSuccess: (data) => {
            setIsPending(false);
            onDidSaved()
            onClose()
            setSuccess('Подраздел успешно создан')
        },
        onError: (_) => setIsPending(false)
    });

    const { mutate: editSubcategoryRequest } = useMutation({
        mutationFn: (data: CreateSubcategoryRequest) => editSubcategory(subcategory?.id ?? '', data),
        onSuccess: (data) => {
            setIsPending(false);
            onDidSaved()
            onClose()
            setInfo('Изменения сохранены')
        },
        onError: (_) => setIsPending(false)
    });


    const saveSubcategory = () => {
        setIsPending(true)
        createSubcategoryRequest({
            title: getRussianOrFirstFieldValue('title'),
            categoryId: parentCategory.id,
            translations: translations
        })
    }

    const updateSubcategory = () => {
        setIsPending(true)
        editSubcategoryRequest({
            title: getRussianOrFirstFieldValue('title'),
            categoryId: parentCategory.id,
            translations: translations
        })
    }

    const trySaveSubcategory = () => {
        if (translations.length === 0) {
            setError('Информация заполнена не для всех выбранных языков')
            return
        }
        if (isAllLanguagesFilled('title')) {
            if (subcategory) {
                // Update
                updateSubcategory()
            } else {
                // Create
                saveSubcategory()
            }
        } else {
            setError('Информация заполнена не для всех выбранных языков')
        }
    };

    useEffect(() => {
        setTranslations(subcategory?.translations ?? [])
        fetchLanguagesRequest({})
    }, [subcategory]);

    useEffect(() => {
        if (subcategory && allLangs) {
            // Если есть translations, используем их
            if (subcategory.translations && subcategory.translations.length > 0) {
                const categoryLangCodes = subcategory.translations.map(el => el.languageIsoCode);
                const selectedLangs = allLangs.filter(lang => categoryLangCodes.includes(lang.isoCode));
                setLangs(selectedLangs);
                setTranslations(subcategory.translations);
            }
            // Если translations пуст, но есть title - считаем что это русский
            else if (subcategory.title) {
                const russianLang = allLangs.find(lang => lang.isoCode === 'ru');
                if (russianLang) {
                    setLangs([russianLang]);
                    setTranslations([{
                        languageIsoCode: 'ru',
                        fields: [{ fieldName: 'title', value: subcategory.title }]
                    }]);
                    setCurrentLangIndex(0);
                }
            }
        } else if (allLangs) {
            const russianLang = allLangs.find(lang => lang.isoCode === 'ru');
            if (russianLang) {
                setLangs([russianLang]);
                setTranslations([{
                    languageIsoCode: 'ru',
                    fields: [{ fieldName: 'title', value: "" }]
                }]);
                setCurrentLangIndex(0);
            }
        }
    }, [allLangs, subcategory]);

    const title = subcategory === undefined ? 'Создать подраздел' : 'Редактировать подраздел'
    const saveButtonTitle = subcategory === undefined ? 'Создать' : 'Сохранить изменения'

    return (
        <ModalSheet
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="middle"
            headerSize="regular"
        >
            {({ setTitle }) => (
                <Stack className='mt-[16px]'>
                    <Stack direction="horizontal" gap='condensed' justify='start'>
                        {langs.map((lang, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentLangIndex(index)}
                                className={`flex items-center gap-[4px] px-2 py-[6px] border-[1px] 
                        ${currentLangIndex === index ? "bg-aloix-black hover:bg-gray-800" : "bg-white hover:bg-gray-50 border-border"}
                         rounded-md  h-8`}>
                                <span className={`${currentLangIndex === index ? "text-white" : "text-aloix-black"} font-medium text-xs`}>{lang.titleRu || lang.titleOrigin}</span>
                            </button>
                        ))}
                        <LangModal
                            filterOpened={openLangModal}
                            setFilterOpened={(val) => setLangModal(val)}
                            selectedLangs={langs}
                            setSelectedFilter={langsDidUpdated}
                            onApply={() => { }}
                            onDismiss={() => { }}
                        />
                    </Stack>
                    {langs.length > 0 &&
                        <>
                            <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                                Наименование
                            </Text>
                            <TextInput
                                type="text"
                                inputMode="text"
                                value={getCurrentFieldValue('title')}
                                onChange={(e) => updateFieldValue('title', e.target.value)}
                                maxLength={200}
                                placeholder="Придумайте название подраздела"
                                size="medium"
                                contrast={!!(getCurrentFieldValue('title') && getCurrentFieldValue('title').trim())}
                            />
                        </>
                    }

                    <Stack direction="horizontal" gap='normal' justify='end'>
                        <div></div>
                        <Button
                            onClick={onClose}
                            size='small'
                            variant="default">
                            Отмена
                        </Button>
                        <Button
                            onClick={trySaveSubcategory}
                            size='small'
                            variant="primary"
                            loading={isPending}
                            disabled={isPending || translations.length === 0}>
                            {saveButtonTitle}
                        </Button>
                    </Stack>
                </Stack>
            )}
        </ModalSheet>
    );
};